import { NetworkStatus, gql, useQuery } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSpinDelay } from "spin-delay";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

import type { Supplier } from "@/apis/gql/types";
import type { InvoiceTypeString } from "@/lib/constants/invoice-type";
import { groupByBooking } from "@/lib/miscellaneous/group-by-booking";
import { useTenant } from "@/providers/session";
interface BookingText {
  lineItemIds: string[];
  bookingText: string;
}

interface BookingTextStore {
  bookingTexts: Record<string, BookingText>;
  getBookingText: (id: string) => BookingText | undefined;
  setBookingText: (id: string, data: BookingText) => void;
}

export const useBookingTextStore = create<BookingTextStore>()(
  devtools((set, get) => ({
    bookingTexts: {},
    getBookingText: (id) => get().bookingTexts[id],
    setBookingText: (id, data) =>
      set((state) => ({
        bookingTexts: {
          ...state.bookingTexts,
          [id]: data,
        },
      })),
  })),
);

export const useGetFastlaneReview = ({
  enabled = true,
  origin,
  supplierId,
  invoiceType,
}: {
  enabled?: boolean;
  origin?: string;
  supplierId?: string;
  invoiceType?: InvoiceTypeString;
}) => {
  const { tenantId } = useTenant();
  const { setBookingText, getBookingText } = useBookingTextStore();
  const hasProcessedCurrentData = useRef<boolean>(false);

  const setupBookingTextCache = useCallback(
    (
      lineItems: {
        lineItemId: string;
        tenantId: string;
        data: any;
      }[],
    ) => {
      const bookings = groupByBooking(lineItems);

      for (const booking of bookings) {
        const bookingText = booking.description;
        const lineItemIds = booking.subRows.map((item: any) => item.id);

        const hasBookingText = getBookingText(booking.id);

        if (!hasBookingText) {
          setBookingText(lineItemIds.join("-"), {
            lineItemIds: lineItemIds,
            bookingText: bookingText,
          });
        }
      }
    },
    [setBookingText, getBookingText],
  );

  const { data, previousData, loading, error, networkStatus } = useQuery(
    FASTLANE_REVIEW_MODE_QUERY,
    {
      variables: {
        tenantId,
        origin,
        supplierId:
          supplierId === "unknown" || supplierId === "" ? null : supplierId,
        ...(invoiceType && { invoiceType }),
      },
      fetchPolicy: "cache-and-network",
      skip: !enabled,
      notifyOnNetworkStatusChange: true,
      errorPolicy: "all",
    },
  );

  useEffect(() => {
    if (
      networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.setVariables
    ) {
      hasProcessedCurrentData.current = false;
    }
  }, [networkStatus]);

  const invoices = useMemo(() => {
    const safeData = data || previousData;

    if (!safeData?.fastlaneReviews?.invoices) return [];

    const invoices = safeData.fastlaneReviews.invoices;

    if (!hasProcessedCurrentData.current) {
      const lineItems = invoices.flatMap((invoice: any) =>
        mapLineItems(invoice, tenantId),
      );

      setupBookingTextCache(lineItems);
      hasProcessedCurrentData.current = true;
    }

    const mappedInvoices = invoices.map((invoice: any) => {
      const bookings = groupByBooking(mapLineItems(invoice, tenantId));

      return {
        id: invoice.invoiceDetailsId,
        invoiceId: invoice.invoiceDetailsId,
        ...invoice,
        subRows: bookings,
      };
    });

    return mappedInvoices;
  }, [data, tenantId]);

  const isLoading = useSpinDelay(loading, { delay: 600, minDuration: 400 });

  const suppliers = useMemo(() => {
    const safeData = data || previousData;
    return safeData?.fastlaneReviews?.suppliers || [];
  }, [data, previousData]);

  return {
    invoices,
    suppliers: suppliers as Supplier[],
    error,
    loading: isLoading,
  };
};

const mapLineItems = (invoice: any, tenantId: string) => {
  return invoice.lineItems.map((item: any) => ({
    tenantId,
    lineItemId: item.lineItemDetailsId,
    data: {
      lineItemDetails: {
        lineItem: item.lineItem,
      },
      invoiceDetails: invoice,
    },
  }));
};

export const FASTLANE_REVIEW_MODE_QUERY = gql`
  query FastlaneReviews(
    $tenantId: String!
    $origin: String
    $supplierId: String
    $invoiceType: String
  ) {
    fastlaneReviews(
      tenantId: $tenantId
      origin: $origin
      supplierId: $supplierId
      invoiceType: $invoiceType
    ) {
      suppliers {
        supplierId
        supplierName
        parentSupplierId
        address {
          addressId
          addressLine1
          city
          state
          postcode
          country
        }
        parentSupplier {
          supplierId
          supplierName
          datevAccountNumber
        }
        datevAccountNumber
        status
      }
      invoices {
        invoiceDetailsId
        invoice {
          invoiceId
          totalAmount
          currency
          ocrReviewed
          invoiceText
          createdAt
          invoiceNo
          isBooking
          isDuplicate
          status
          invoiceIntent
          totalLineItems
          supplierId
        }
        supplier {
          supplierId
          supplierName
          parentSupplierId
          address {
            addressId
            addressLine1
            city
            state
            postcode
            country
          }
          parentSupplier {
            supplierId
            supplierName
            datevAccountNumber
          }
          datevAccountNumber
          status
        }
        tags {
          tagId
          tagName
          llmRule
          details
          approval
          createdAt
          updatedAt
        }
        lineItems {
          lineItemDetailsId
          lineItem {
            lineItemId
            costCenterId
            aiDescription
            description
            amount
            invoiceId
            name
            vatRate
            startDate
            endDate
            accountJustification
            index
            accountId
            accountConfidence
            accountReviewed
            taxCodeId
            taxCodeConfidence
            bookingText
            buJustification
            createdAt
            createdBy
            unlinked
          }
          costCenter {
            costCenterId
            name
            description
            identifier
          }
          expenseAccount {
            accountId
            accountNo
            accountName
            accountDescription
            accountType
            automatic
            parentAccountId
            min
            max
            buExport
            serviceDateExport
            faktor2
          }
          taxCode {
            taxCodeId
            name
            description
            datevBu
            vatRate
          }
        }
      }
    }
  }
`;
